//pages/index.jsx

import { useState } from 'react';
import { getPageSEO } from '../utils/helpers/seo';
// Critical above-the-fold components (loaded immediately)
import { FooterSection, HeaderSection, SEOWrapper } from '../components/layout';
import {  GeneralSection, FleetSection, ServicesSection, TestimonialsSection, HowItWorksSection, FeaturesSection, CTASection, TrustedBySection, FAQSection, LocationsSection, HeroSectionWithForm } from '../components/sections';
import { usePageData } from '../utils/hooks';

import { Loader } from '../components/common';
import { fetchConfigPage, fetchHomePage } from '../utils/api/api';

export default function Home({ initialData, error }) {
    const [strapiData, setStrapiData] = useState(initialData);
    const [isLoading, setIsLoading] = useState(!initialData);
    const [apiError, setError] = useState(error);

    // Extract all data using custom hook
    const {
        configData,
        pageData,
        header,
        footer,
        heroSection,
        generalContent,
        fleetData,
        trustedByData,
        servicesData,
        testimonialsData,
        howItWorksData,
        featuresData,
        ctaData,
        locationsData
    } = usePageData(strapiData);

    // Get FAQ data with special structure for home page
    const faqData = {
        order: pageData.Faqs?.order || pageData.FAQ?.order || pageData.faqs?.order || 12,
        title: pageData.Faqs?.title || pageData.FAQ?.title || pageData.faqs?.title || 'FAQs',
        description: pageData.Faqs?.description || pageData.FAQ?.description || pageData.faqs?.description || '',
        faqs: pageData.Faqs?.faqs || pageData.FAQ?.faqs || pageData.faqs?.faqs || [],
    };

    // Create sections array with order - handle nested arrays
    const createOrderedSections = () => {
        const allSections = [];
        
        // Process GeneralSection - extract individual items
        if (generalContent) {
            const generalItems = Array.isArray(generalContent) ? generalContent : [generalContent];
            generalItems.forEach((item, index) => {
                allSections.push({
                    component: <GeneralSection data={[item]} globalIndex={index} />,
                    order: item.order || item.Order || 1 + (index * 0.1) // Add small offset for same order
                });
            });
        }
        
        // Add other sections
        if (trustedByData) {
            allSections.push({
                component: <TrustedBySection data={trustedByData} />,
                order: trustedByData.order || trustedByData.Order || 2
            });
        }
        
        if (featuresData) {
            allSections.push({
                component: <FeaturesSection data={featuresData} />,
                order: featuresData.order || featuresData.Order || 3
            });
        }
        
        if (fleetData) {
            allSections.push({
                component: <FleetSection fleetData={fleetData} />,
                order: fleetData.order || fleetData.Order || 4
            });
        }
        
        if (locationsData) {
            allSections.push({
                component: <LocationsSection data={locationsData} />,
                order: locationsData.order || locationsData.Order || 4.5
            });
        }
        
        if (servicesData) {
            allSections.push({
                component: <ServicesSection data={servicesData} />,
                order: servicesData.order || servicesData.Order || 5
            });
        }
        
        if (howItWorksData) {
            allSections.push({
                component: <HowItWorksSection data={howItWorksData} />,
                order: howItWorksData.order || howItWorksData.Order || 6
            });
        }
        
        if (testimonialsData) {
            allSections.push({
                component: <TestimonialsSection data={testimonialsData} />,
                order: testimonialsData.order || testimonialsData.Order || 7
            });
        }
        
        if (faqData) {
            allSections.push({
                component: <FAQSection data={faqData} />,
                order: faqData.order || faqData.Order || 12
            });
        }
        
        if (ctaData) {
            allSections.push({
                component: <CTASection data={ctaData} />,
                order: ctaData.order || ctaData.Order || 13
            });
        }
        
        // Sort by order
        return allSections.sort((a, b) => a.order - b.order);
    };

    const sections = createOrderedSections();
    
    if (isLoading) {
        return <Loader type="page" message="Loading..." fullPage={true} />;
    }

    if (apiError) {
        return <Loader type="error" message="Error loading content" errorMessage={apiError} fullPage={true} />;
    }

    // Generate SEO data for home page
    const seoData = getPageSEO(pageData, configData, strapiData.pageUrl);


    return (
        <>
            <SEOWrapper 
                pageData={pageData}
                configData={configData}
                pageUrl={strapiData.pageUrl}
                seoData={seoData}
            />

            {/* Critical above-the-fold components load immediately */}
            <HeaderSection data={header} pageUrl={strapiData.pageUrl} phoneNumber={pageData.EnhancedHeroSection?.phone} />
            <HeroSectionWithForm data={heroSection} phoneNumber={header?.Contact} />

            <section style={{ display: 'flex', flexDirection: 'column' }}>
                {/* All sections rendered in order */}
                {sections.map((section, index) => (
                    <div key={index} style={{ order: section.order }}>
                        {section.component}
                    </div>
                ))}
            </section>

            {/* Footer Section - lazy loaded */}
            <FooterSection data={footer} />
        </>
    );
}

export async function getServerSideProps({ req, res }) {
    // Add caching headers for better performance
    res.setHeader('Cache-Control', 'public, s-maxage=300, stale-while-revalidate=600');
    try {
        // Parallelize API calls to reduce server response time
        const [pageData, configData] = await Promise.all([
            fetchHomePage(),
            fetchConfigPage()
        ]);



        if (
        !pageData.data || // covers null/undefined
        typeof pageData.data !== 'object' || // covers non-objects
        (Array.isArray(pageData.data) && pageData.data.every(item => item == null)) // covers [null], [undefined]
        ) {
        return {
            props: {
            initialData: null,
            notFound: true,
            error: null,
            },
        };
        }




        return {
            props: {
                initialData: {
                    configData: configData,
                    pageData: pageData.data,
                    pageUrl: '/',
                },
                error: null,
            },
        };
    } catch (err) {
        return {
            props: {
                initialData: null,
                error: err.message,
            },
        };
    }
}

